import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, type FieldPath, type UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import type { CountryCode } from 'libphonenumber-js'

import { getCheckoutPath, getCheckoutSuccessPath } from '@/checkout-paths'
import {
    buildContactInfoPayload,
    CONTACT_INFO_FIELDS,
    hasMissingRequiredContactValues,
    hasMissingRequiredInvoiceValues,
    INVOICE_FIELDS,
    OPTIONAL_INVOICE_FIELDS_WHEN_REQUIRED,
    REQUIRED_CONTACT_FIELDS,
    type CheckoutContactInfoField,
} from '@/components/checkout/contact-info/utils'
import {
    getEmailDomainSuggestion,
    type EmailDomainSuggestion,
} from '@/components/checkout/contact-info/email-domain-suggestion'
import { isSupportedInvoiceCountryCode } from '@/components/checkout/country-options'
import {
    useCreateStripeCheckoutSession,
    usePayByBenefitCode,
    useUpdateReservationContactInfo,
} from '@/hooks/api/use-reservation-checkout'
import {
    getStripeCheckoutUiModeForCurrentDevice,
    presentNativeStripePaymentSheet,
    resolveStripeCheckoutAction,
    type NativeStripePaymentSheetInput,
    type StripeCheckoutUiMode,
} from '@/stripe-checkout'
import type { CheckoutBeginPaymentCustomer } from '@/types/checkout-tracking'
import { VehicleEntryMode, type UserVehicle } from '@/types'
import type {
    BenefitCodePaymentPayload,
    CheckoutContactInfoValues,
    CheckoutVehicleInfoValues,
    StripeCheckoutSessionPayload,
    UpdateReservationContactInfoMutationInput,
} from '@/types/reservation-checkout'
import {
    buildVehiclePayload,
    getInitialVehicleSelection,
    getSelectedSavedCar,
    hasMissingVehicleNumberPlate,
    shouldShowVehicleEditor,
    VEHICLE_INFO_FIELDS,
    type CheckoutVehicleSelection,
} from '@/components/checkout/vehicle-info/utils'
import type { CheckoutMode } from './types'
import {
    formatInternationalPhoneValue,
    getBrowserLanguages,
    getStoredPhoneCountry,
    isPossiblePhoneValue,
    resolveDefaultPhoneCountry,
    setStoredPhoneCountry,
} from '@/components/checkout/floating-label-phone-input/utils'

type CheckoutActionScope = 'contact' | 'vehicle' | 'pay_stripe' | 'pay_benefit_code'

export type CheckoutValidationSection = 'contact' | 'vehicle' | 'disclaimer'

type ContactSnapshot = {
    formValues: CheckoutContactInfoValues
    summaryValues: CheckoutContactInfoValues
    showPhoneNumber2: boolean
}

type VehicleSnapshot = {
    formValues: CheckoutVehicleInfoValues
    summaryValues: CheckoutVehicleInfoValues
    vehicleEntryMode: VehicleEntryMode
    vehicleSelection: CheckoutVehicleSelection
    missingPlateDismissed: boolean
}

type ContactServerState = {
    summaryValues: CheckoutContactInfoValues
    formValues: CheckoutContactInfoValues
}

type VehicleServerState = {
    values: CheckoutVehicleInfoValues
    currentUserCars: UserVehicle[]
    shouldPrefillCheckoutInfo: boolean
}

export type CheckoutValidationMessages = {
    requiredContact: string
    invalidContactEmail: string
    invalidContactPhone: string
    requiredVehicle: string
    requiredDisclaimer: string
}

export type CheckoutFormValues = CheckoutContactInfoValues & CheckoutVehicleInfoValues & {
    show_phone_number_2: boolean
    vehicle_entry_mode: VehicleEntryMode
    vehicle_selection: CheckoutVehicleSelection
    checkout_disclaimer_accepted: boolean
}

export type CheckoutFormController = ReturnType<typeof useCheckoutFormController>

export async function continueToStripeCheckout({
    isPwa,
    onEmbeddedCheckout,
    createStripeCheckoutSession,
    onCheckoutError,
    resolveCheckoutUiMode = getStripeCheckoutUiModeForCurrentDevice,
    resolveCheckoutAction = resolveStripeCheckoutAction,
    presentNativePaymentSheet = presentNativeStripePaymentSheet,
    buildNativePaymentSheetInput,
    onNativeCheckoutCompleted,
    onNativeCheckoutCanceled,
    replaceLocation = url => {
        window.location.replace(url)
    },
}: {
    isPwa?: boolean
    onEmbeddedCheckout?: () => void
    createStripeCheckoutSession: (input: { checkoutUiMode: Exclude<StripeCheckoutUiMode, 'embedded'> }) => Promise<{ data: StripeCheckoutSessionPayload }>
    onCheckoutError: () => void
    resolveCheckoutUiMode?: (isPwa: boolean) => StripeCheckoutUiMode
    resolveCheckoutAction?: typeof resolveStripeCheckoutAction
    presentNativePaymentSheet?: (input: NativeStripePaymentSheetInput) => Promise<{ status: 'completed' | 'canceled' }>
    buildNativePaymentSheetInput?: (input: {
        paymentId: number
        paymentIntentClientSecret: string
    }) => NativeStripePaymentSheetInput
    onNativeCheckoutCompleted?: (paymentId: number) => void
    onNativeCheckoutCanceled?: () => void
    replaceLocation?: (url: string) => void
}) {
    const checkoutUiMode = resolveCheckoutUiMode(Boolean(isPwa))

    if (checkoutUiMode === 'embedded') {
        onEmbeddedCheckout?.()
        return
    }

    const startHostedFallback = async () => {
        const response = await createStripeCheckoutSession({ checkoutUiMode: 'hosted' })
        const action = resolveCheckoutAction(response.data)

        if (action.type === 'redirect') {
            replaceLocation(action.url)
            return true
        }

        return false
    }

    const response = await createStripeCheckoutSession({ checkoutUiMode })
    const action = resolveCheckoutAction(response.data)

    if (action.type === 'redirect') {
        replaceLocation(action.url)
        return
    }

    if (action.type === 'native' && buildNativePaymentSheetInput) {
        try {
            const result = await presentNativePaymentSheet(buildNativePaymentSheetInput({
                paymentId: action.paymentId,
                paymentIntentClientSecret: action.paymentIntentClientSecret,
            }))

            if (result.status === 'completed') {
                onNativeCheckoutCompleted?.(action.paymentId)
                return
            }

            onNativeCheckoutCanceled?.()
            return
        } catch {
            if (await startHostedFallback()) {
                return
            }
        }
    }

    onCheckoutError()
}

const CONTACT_BASE_FIELD_NAMES = [
    'name',
    'email',
    'phone_number',
] as const satisfies ReadonlyArray<FieldPath<CheckoutFormValues>>

const CONTACT_SECTION_FIELD_NAMES = [
    ...CONTACT_INFO_FIELDS,
    'requested_invoice',
    'show_phone_number_2',
] as const satisfies ReadonlyArray<FieldPath<CheckoutFormValues>>

const VEHICLE_SECTION_FIELD_NAMES = [
    ...VEHICLE_INFO_FIELDS,
    'vehicle_entry_mode',
    'vehicle_selection',
] as const satisfies ReadonlyArray<FieldPath<CheckoutFormValues>>

const VEHICLE_VALIDATION_FIELD_NAMES = [
    'vehicle_selection',
    ...VEHICLE_INFO_FIELDS,
] as const satisfies ReadonlyArray<FieldPath<CheckoutFormValues>>

const DISCLAIMER_FIELD_NAME = 'checkout_disclaimer_accepted' as const satisfies FieldPath<CheckoutFormValues>

export function getFirstInvalidCheckoutSection({
    contactIsValid,
    vehicleIsValid,
    disclaimerIsValid,
}: {
    contactIsValid: boolean
    vehicleIsValid: boolean
    disclaimerIsValid: boolean
}): CheckoutValidationSection | null {
    if (!contactIsValid) {
        return 'contact'
    }

    if (!vehicleIsValid) {
        return 'vehicle'
    }

    if (!disclaimerIsValid) {
        return 'disclaimer'
    }

    return null
}

export function shouldEditCheckoutContactSection({
    mode,
    isEditing,
    summaryValues,
    invoiceRequired,
}: {
    mode: CheckoutMode
    isEditing: boolean
    summaryValues: CheckoutContactInfoValues
    invoiceRequired: boolean
}): boolean {
    return (
        mode === 'single_submit'
        || isEditing
        || hasMissingRequiredContactValues(summaryValues)
        || hasMissingRequiredInvoiceValues(summaryValues, invoiceRequired)
    )
}

export function getCheckoutValidationMessages(t: (key: string) => string): CheckoutValidationMessages {
    return {
        requiredContact: t('checkout.contactInfo.validation.required'),
        invalidContactEmail: t('checkout.contactInfo.validation.invalidEmail'),
        invalidContactPhone: t('checkout.contactInfo.validation.invalidPhone'),
        requiredVehicle: t('checkout.vehicleInfo.validation.required'),
        requiredDisclaimer: t('checkout.disclaimer.validation.required'),
    }
}

function normalizeSavedContactValues(payload: UpdateReservationContactInfoMutationInput): CheckoutContactInfoValues {
    return {
        name: payload.name ?? '',
        email: payload.email ?? '',
        phone_number: payload.phone_number ?? '',
        phone_number_2: payload.phone_number_2 ?? '',
        invoice_title: payload.invoice_title ?? '',
        invoice_street: payload.invoice_street ?? '',
        invoice_city: payload.invoice_city ?? '',
        invoice_zip_code: payload.invoice_zip_code ?? '',
        invoice_country: payload.invoice_country ?? '',
        invoice_ic: payload.invoice_ic ?? '',
        invoice_dic: payload.invoice_dic ?? '',
        requested_invoice: payload.requested_invoice ?? false,
    }
}

function normalizeSavedVehicleValues(payload: UpdateReservationContactInfoMutationInput): CheckoutVehicleInfoValues {
    return {
        number_plate: payload.number_plate ?? '',
        car_color: payload.car_color ?? '',
    }
}

function createContactSnapshot({
    formValues,
    summaryValues,
}: {
    formValues: CheckoutContactInfoValues
    summaryValues: CheckoutContactInfoValues
}): ContactSnapshot {
    const formattedFormValues = formatContactFormPhoneValues(formValues)

    return {
        formValues: formattedFormValues,
        summaryValues,
        showPhoneNumber2: formattedFormValues.phone_number_2.trim() !== '',
    }
}

function formatContactFormPhoneValues(
    values: CheckoutContactInfoValues,
): CheckoutContactInfoValues {
    return {
        ...values,
        phone_number: formatInternationalPhoneValue(values.phone_number),
        phone_number_2: formatInternationalPhoneValue(values.phone_number_2),
    }
}

function createVehicleSnapshot({
    formValues,
    summaryValues,
    currentUserCars,
    shouldPrefillCheckoutInfo,
    missingPlateDismissed,
}: {
    formValues: CheckoutVehicleInfoValues
    summaryValues: CheckoutVehicleInfoValues
    currentUserCars: UserVehicle[]
    shouldPrefillCheckoutInfo: boolean
    missingPlateDismissed: boolean
}): VehicleSnapshot {
    return {
        formValues,
        summaryValues,
        vehicleEntryMode: VehicleEntryMode.now,
        vehicleSelection: getInitialVehicleSelection({
            values: formValues,
            currentUserCars,
            shouldPrefillCheckoutInfo,
        }),
        missingPlateDismissed,
    }
}

function setContactFormValues(
    form: UseFormReturn<CheckoutFormValues>,
    snapshot: ContactSnapshot,
) {
    setSectionFormValues(form, {
        ...snapshot.formValues,
        show_phone_number_2: snapshot.showPhoneNumber2,
    }, CONTACT_SECTION_FIELD_NAMES)
}

function setVehicleFormValues(
    form: UseFormReturn<CheckoutFormValues>,
    snapshot: VehicleSnapshot,
) {
    setSectionFormValues(form, {
        ...snapshot.formValues,
        vehicle_entry_mode: snapshot.vehicleEntryMode,
        vehicle_selection: snapshot.vehicleSelection,
    }, VEHICLE_SECTION_FIELD_NAMES)
}

function setSectionFormValues(
    form: UseFormReturn<CheckoutFormValues>,
    values: Partial<CheckoutFormValues>,
    fieldNames: ReadonlyArray<FieldPath<CheckoutFormValues>>,
) {
    for (const fieldName of fieldNames) {
        const value = values[fieldName]

        if (value !== undefined) {
            form.setValue(fieldName, value)
        }
    }
}

function clearSectionErrors(
    form: UseFormReturn<CheckoutFormValues>,
    fieldNames: ReadonlyArray<FieldPath<CheckoutFormValues>>,
) {
    form.clearErrors(fieldNames)
}

function getContactFieldNames(values: CheckoutFormValues): Array<FieldPath<CheckoutFormValues>> {
    const fieldNames: Array<FieldPath<CheckoutFormValues>> = [...CONTACT_BASE_FIELD_NAMES]

    if (values.show_phone_number_2) {
        fieldNames.push('phone_number_2')
    }

    if (values.requested_invoice) {
        fieldNames.push(...INVOICE_FIELDS)
    }

    return fieldNames
}

function getVehicleSectionFieldNames(values: CheckoutFormValues): Array<FieldPath<CheckoutFormValues>> {
    if (values.vehicle_entry_mode === VehicleEntryMode.later) {
        return []
    }

    if (values.vehicle_selection !== 'custom') {
        return ['vehicle_selection']
    }

    return ['number_plate', 'car_color']
}

function getFirstErrorField(
    fieldNames: readonly FieldPath<CheckoutFormValues>[],
    getFieldState: UseFormReturn<CheckoutFormValues>['getFieldState'],
): FieldPath<CheckoutFormValues> | null {
    for (const fieldName of fieldNames) {
        if (getFieldState(fieldName).error) {
            return fieldName
        }
    }

    return null
}

function buildSavedContactSnapshot(
    payload: UpdateReservationContactInfoMutationInput,
): ContactSnapshot {
    const savedValues = normalizeSavedContactValues(payload)

    return createContactSnapshot({
        formValues: savedValues,
        summaryValues: savedValues,
    })
}

function buildSavedVehicleSnapshot({
    values,
    currentUserCars,
    shouldPrefillCheckoutInfo,
}: {
    values: CheckoutFormValues
    currentUserCars: UserVehicle[]
    shouldPrefillCheckoutInfo: boolean
}): VehicleSnapshot {
    const savedValues = normalizeSavedVehicleValues(buildVehiclePayload({
        values,
        vehicleEntryMode: values.vehicle_entry_mode,
        selectedSavedCar: getSelectedSavedCar({
            currentUserCars,
            selection: values.vehicle_selection,
        }),
    }))

    return createVehicleSnapshot({
        formValues: savedValues,
        summaryValues: savedValues,
        currentUserCars,
        shouldPrefillCheckoutInfo,
        missingPlateDismissed: values.vehicle_entry_mode === VehicleEntryMode.later,
    })
}

export function buildCheckoutFormDefaultValues({
    contactFormValues,
    vehicleValues,
    currentUserCars,
    shouldPrefillCheckoutInfo,
}: {
    contactFormValues: CheckoutContactInfoValues
    vehicleValues: CheckoutVehicleInfoValues
    currentUserCars: UserVehicle[]
    shouldPrefillCheckoutInfo: boolean
}): CheckoutFormValues {
    return {
        ...formatContactFormPhoneValues(contactFormValues),
        show_phone_number_2: contactFormValues.phone_number_2.trim() !== '',
        number_plate: vehicleValues.number_plate,
        car_color: vehicleValues.car_color,
        vehicle_entry_mode: VehicleEntryMode.now,
        vehicle_selection: getInitialVehicleSelection({
            values: vehicleValues,
            currentUserCars,
            shouldPrefillCheckoutInfo,
        }),
        checkout_disclaimer_accepted: false,
    }
}

function areContactInfoValuesEqual(
    left: CheckoutContactInfoValues,
    right: CheckoutContactInfoValues,
) {
    return (
        CONTACT_INFO_FIELDS.every(fieldName => left[fieldName] === right[fieldName])
        && left.requested_invoice === right.requested_invoice
    )
}

function areVehicleInfoValuesEqual(
    left: CheckoutVehicleInfoValues,
    right: CheckoutVehicleInfoValues,
) {
    return VEHICLE_INFO_FIELDS.every(fieldName => left[fieldName] === right[fieldName])
}

function areUserVehicleListsEqual(
    left: UserVehicle[],
    right: UserVehicle[],
) {
    if (left.length !== right.length) {
        return false
    }

    return left.every((car, index) => {
        const rightCar = right[index]

        return (
            rightCar !== undefined
            && car.id === rightCar.id
            && car.name === rightCar.name
            && car.number_plate === rightCar.number_plate
            && car.color === rightCar.color
        )
    })
}

export function areContactServerStatesEqual(
    left: ContactServerState,
    right: ContactServerState,
) {
    return (
        areContactInfoValuesEqual(left.summaryValues, right.summaryValues)
        && areContactInfoValuesEqual(left.formValues, right.formValues)
    )
}

export function areVehicleServerStatesEqual(
    left: VehicleServerState,
    right: VehicleServerState,
) {
    return (
        areVehicleInfoValuesEqual(left.values, right.values)
        && areUserVehicleListsEqual(left.currentUserCars, right.currentUserCars)
        && left.shouldPrefillCheckoutInfo === right.shouldPrefillCheckoutInfo
    )
}

export function buildServerSyncedContactSnapshot({
    summaryValues,
    formValues,
}: {
    summaryValues: CheckoutContactInfoValues
    formValues: CheckoutContactInfoValues
}) {
    return createContactSnapshot({
        formValues,
        summaryValues,
    })
}

export function buildServerSyncedVehicleSnapshot({
    values,
    currentUserCars,
    shouldPrefillCheckoutInfo,
    previousMissingPlateDismissed,
}: {
    values: CheckoutVehicleInfoValues
    currentUserCars: UserVehicle[]
    shouldPrefillCheckoutInfo: boolean
    previousMissingPlateDismissed: boolean
}) {
    return createVehicleSnapshot({
        formValues: values,
        summaryValues: values,
        currentUserCars,
        shouldPrefillCheckoutInfo,
        missingPlateDismissed: hasMissingVehicleNumberPlate(values)
            ? previousMissingPlateDismissed
            : false,
    })
}

export function buildCheckoutPayload({
    values,
    currentUserCars,
    defaultPhoneCountry,
}: {
    values: CheckoutFormValues
    currentUserCars: UserVehicle[]
    defaultPhoneCountry: CountryCode
}): UpdateReservationContactInfoMutationInput {
    return {
        ...buildContactInfoPayload({
            values,
            showPhoneNumber2: values.show_phone_number_2,
            defaultPhoneCountry,
        }),
        ...buildVehiclePayload({
            values,
            vehicleEntryMode: values.vehicle_entry_mode,
            selectedSavedCar: getSelectedSavedCar({
                currentUserCars,
                selection: values.vehicle_selection,
            }),
        }),
    }
}

export function useCheckoutFormController({
    mode,
    contactSummaryValues,
    contactFormValues,
    invoiceRequired,
    vehicleValues,
    currentUserCars,
    shouldPrefillCheckoutInfo,
    phoneCountryBillingHint,
    isPwa,
    onPay,
    onBeginPayment,
}: {
    mode: CheckoutMode
    contactSummaryValues: CheckoutContactInfoValues
    contactFormValues: CheckoutContactInfoValues
    invoiceRequired: boolean
    vehicleValues: CheckoutVehicleInfoValues
    currentUserCars: UserVehicle[]
    shouldPrefillCheckoutInfo: boolean
    phoneCountryBillingHint: string | null
    isPwa?: boolean
    onPay?: () => void
    onBeginPayment?: (customer: CheckoutBeginPaymentCustomer) => void
}) {
    const { t, i18n } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const validationMessages = useMemo(() => getCheckoutValidationMessages(t), [t])
    const form = useForm<CheckoutFormValues>({
        defaultValues: buildCheckoutFormDefaultValues({
            contactFormValues,
            vehicleValues,
            currentUserCars,
            shouldPrefillCheckoutInfo,
        }),
    })
    const [contactIsEditing, setContactIsEditing] = useState(false)
    const [vehicleIsEditing, setVehicleIsEditing] = useState(false)
    const [defaultPhoneCountry, setDefaultPhoneCountry] = useState<CountryCode>(() => (
        resolveDefaultPhoneCountry({
            phoneValues: [contactFormValues.phone_number, contactFormValues.phone_number_2],
            rememberedCountry: getStoredPhoneCountry(),
            billingCountry: phoneCountryBillingHint,
            browserLanguages: getBrowserLanguages(),
        })
    ))
    const [emailDomainSuggestion, setEmailDomainSuggestion] = useState<EmailDomainSuggestion | null>(null)
    const [contactSnapshot, setContactSnapshot] = useState<ContactSnapshot>(() => createContactSnapshot({
        formValues: contactFormValues,
        summaryValues: contactSummaryValues,
    }))
    const [vehicleSnapshot, setVehicleSnapshot] = useState<VehicleSnapshot>(() => createVehicleSnapshot({
        formValues: vehicleValues,
        summaryValues: vehicleValues,
        currentUserCars,
        shouldPrefillCheckoutInfo,
        missingPlateDismissed: false,
    }))
    const [requestInProgress, setRequestInProgress] = useState(false)
    const [actionError, setActionError] = useState<{
        scope: CheckoutActionScope | null
        message: string | null
    }>({
        scope: null,
        message: null,
    })
    const [activeAction, setActiveAction] = useState<CheckoutActionScope | null>(null)
    const contactSectionRef = useRef<HTMLDivElement | null>(null)
    const vehicleSectionRef = useRef<HTMLDivElement | null>(null)
    const disclaimerSectionRef = useRef<HTMLDivElement | null>(null)
    const activeActionRef = useRef<CheckoutActionScope | null>(null)
    const acknowledgedEmailDomainRef = useRef<string | null>(null)
    const contactServerState = useMemo(() => ({
        summaryValues: contactSummaryValues,
        formValues: contactFormValues,
    }), [contactFormValues, contactSummaryValues])
    const vehicleServerState = useMemo(() => ({
        values: vehicleValues,
        currentUserCars,
        shouldPrefillCheckoutInfo,
    }), [currentUserCars, shouldPrefillCheckoutInfo, vehicleValues])
    const lastContactServerStateRef = useRef(contactServerState)
    const lastVehicleServerStateRef = useRef(vehicleServerState)
    const updateReservationContactInfo = useUpdateReservationContactInfo({
        setRequestInProgress,
        onErrorCallback: message => {
            setActionError({
                scope: activeActionRef.current,
                message,
            })
        },
        onSuccessCallback: () => {
            setActionError({
                scope: null,
                message: null,
            })
        },
    })
    const payByBenefitCode = usePayByBenefitCode({
        setRequestInProgress,
        onErrorCallback: message => {
            setActionError({
                scope: activeActionRef.current,
                message,
            })
        },
        onSuccessCallback: () => {
            setActionError({
                scope: null,
                message: null,
            })
        },
    })
    const createStripeCheckoutSession = useCreateStripeCheckoutSession({
        setRequestInProgress,
        onErrorCallback: message => {
            setActionError({
                scope: activeActionRef.current,
                message,
            })
        },
        onSuccessCallback: () => {
            setActionError({
                scope: null,
                message: null,
            })
        },
    })

    const contactShouldEdit = shouldEditCheckoutContactSection({
        mode,
        isEditing: contactIsEditing,
        summaryValues: contactSnapshot.summaryValues,
        invoiceRequired,
    })
    const vehicleShouldEdit = mode === 'single_submit'
        || shouldShowVehicleEditor({
            values: vehicleSnapshot.summaryValues,
            edit: vehicleIsEditing,
            missingPlateDismissed: vehicleSnapshot.missingPlateDismissed,
        })

    useEffect(() => {
        if (areContactServerStatesEqual(contactServerState, lastContactServerStateRef.current)) {
            return
        }

        lastContactServerStateRef.current = contactServerState

        const nextSnapshot = buildServerSyncedContactSnapshot({
            formValues: contactServerState.formValues,
            summaryValues: contactServerState.summaryValues,
        })

        setContactSnapshot(nextSnapshot)

        if (!contactShouldEdit) {
            setContactFormValues(form, nextSnapshot)
        }
    }, [
        contactServerState,
        contactShouldEdit,
        form,
    ])

    useEffect(() => {
        if (areVehicleServerStatesEqual(vehicleServerState, lastVehicleServerStateRef.current)) {
            return
        }

        lastVehicleServerStateRef.current = vehicleServerState

        const nextSnapshot = buildServerSyncedVehicleSnapshot({
            values: vehicleServerState.values,
            currentUserCars: vehicleServerState.currentUserCars,
            shouldPrefillCheckoutInfo: vehicleServerState.shouldPrefillCheckoutInfo,
            previousMissingPlateDismissed: vehicleSnapshot.missingPlateDismissed,
        })

        setVehicleSnapshot(nextSnapshot)

        if (!vehicleShouldEdit) {
            setVehicleFormValues(form, nextSnapshot)
        }
    }, [
        form,
        vehicleShouldEdit,
        vehicleServerState,
        vehicleSnapshot.missingPlateDismissed,
    ])

    const clearScopeError = (scope: CheckoutActionScope) => {
        setActionError(currentError => (
            currentError.scope === scope
                ? {
                    scope: null,
                    message: null,
                }
                : currentError
        ))
    }

    const buildNormalizedContactPayload = (
        values: CheckoutFormValues,
    ): UpdateReservationContactInfoMutationInput => buildContactInfoPayload({
        values,
        showPhoneNumber2: values.show_phone_number_2,
        defaultPhoneCountry,
    })

    const selectPhoneCountry = (country: CountryCode) => {
        setDefaultPhoneCountry(country)
        setStoredPhoneCountry(country)
    }

    const scrollToSection = (scope: CheckoutValidationSection) => {
        const sectionRef = scope === 'contact'
            ? contactSectionRef
            : scope === 'vehicle'
                ? vehicleSectionRef
                : disclaimerSectionRef

        sectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }

    const clearEmailDomainSuggestion = () => {
        setEmailDomainSuggestion(null)
        acknowledgedEmailDomainRef.current = null
    }

    const showEmailDomainSuggestion = (email: string): EmailDomainSuggestion | null => {
        const correctedEmail = getEmailDomainSuggestion(email)
        const suggestion = correctedEmail
            ? {
                source: email,
                correctedEmail,
            }
            : null

        setEmailDomainSuggestion(suggestion)
        return suggestion
    }

    const checkEmailDomainSuggestion = (email: string): EmailDomainSuggestion | null => {
        const suggestion = showEmailDomainSuggestion(email)
        acknowledgedEmailDomainRef.current = suggestion?.source ?? null
        return suggestion
    }

    const shouldPauseForEmailDomainSuggestion = (email: string): boolean => {
        const suggestionWasAcknowledged = acknowledgedEmailDomainRef.current === email
        const suggestion = showEmailDomainSuggestion(email)

        if (!suggestion) {
            acknowledgedEmailDomainRef.current = null
            return false
        }

        if (suggestionWasAcknowledged) {
            return false
        }

        acknowledgedEmailDomainRef.current = email
        return true
    }

    const focusFirstContactError = () => {
        const firstErrorField = getFirstErrorField([
            ...CONTACT_BASE_FIELD_NAMES,
            'phone_number_2',
            ...INVOICE_FIELDS,
        ], form.getFieldState)

        if (firstErrorField) {
            form.setFocus(firstErrorField)
        }
    }

    const focusFirstVehicleError = () => {
        const firstErrorField = getFirstErrorField(VEHICLE_VALIDATION_FIELD_NAMES, form.getFieldState)

        if (firstErrorField && firstErrorField !== 'vehicle_selection') {
            form.setFocus(firstErrorField)
        }
    }

    const openContactForValidation = () => {
        setContactIsEditing(true)
        scrollToSection('contact')
    }

    const openVehicleForValidation = () => {
        setVehicleIsEditing(true)
        scrollToSection('vehicle')
    }

    const validateContactSection = async () => {
        const currentValues = form.getValues()

        clearScopeError('contact')
        form.clearErrors(getContactFieldNames(currentValues))

        // A non-editing section is treated as valid during checkout validation.
        if (!contactShouldEdit) {
            return true
        }

        return form.trigger(getContactFieldNames(currentValues), { shouldFocus: false })
    }

    const validateVehicleSection = async () => {
        const currentValues = form.getValues()

        clearScopeError('vehicle')
        form.clearErrors(getVehicleSectionFieldNames(currentValues))

        // A non-editing section is treated as valid during checkout validation.
        if (!vehicleShouldEdit) {
            return true
        }

        if (currentValues.vehicle_entry_mode === VehicleEntryMode.later) {
            return true
        }

        if (currentValues.vehicle_selection !== 'custom') {
            const selectedSavedCar = getSelectedSavedCar({
                currentUserCars,
                selection: currentValues.vehicle_selection,
            })

            if (selectedSavedCar) {
                return true
            }

            form.setError('vehicle_selection', {
                type: 'validate',
                message: validationMessages.requiredVehicle,
            })
            return false
        }

        return form.trigger(['number_plate'], { shouldFocus: false })
    }

    const runScopedAction = async <T,>({
        scope,
        action,
    }: {
        scope: CheckoutActionScope
        action: () => Promise<T>
    }) => {
        setActionError({
            scope: null,
            message: null,
        })
        setActiveAction(scope)
        activeActionRef.current = scope
        setRequestInProgress(true)

        try {
            return await action()
        } finally {
            setActiveAction(null)
            activeActionRef.current = null
        }
    }

    const persistPayload = async ({
        scope,
        payload,
    }: {
        scope: CheckoutActionScope
        payload: UpdateReservationContactInfoMutationInput
    }) => {
        try {
            await runScopedAction({
                scope,
                action: async () => await updateReservationContactInfo.mutateAsync(payload),
            })
            return true
        } catch {
            return false
        }
    }

    const syncSavedCheckoutSnapshots = () => {
        const currentValues = form.getValues()

        setContactSnapshot(buildSavedContactSnapshot(buildNormalizedContactPayload(currentValues)))
        setVehicleSnapshot(buildSavedVehicleSnapshot({
            values: currentValues,
            currentUserCars,
            shouldPrefillCheckoutInfo,
        }))
    }

    const prepareCheckoutForPayment = async (scope: 'pay_stripe' | 'pay_benefit_code') => {
        clearScopeError(scope)
        form.clearErrors(DISCLAIMER_FIELD_NAME)

        const contactIsValid = await validateContactSection()
        const vehicleIsValid = await validateVehicleSection()
        const currentValues = form.getValues()
        const disclaimerValidationResult = contactIsValid && vehicleIsValid
            ? validateCheckoutDisclaimerAccepted(
                currentValues.checkout_disclaimer_accepted,
                validationMessages,
            )
            : true
        const disclaimerIsValid = disclaimerValidationResult === true

        if (!disclaimerIsValid) {
            form.setError(DISCLAIMER_FIELD_NAME, {
                type: 'validate',
                message: disclaimerValidationResult,
            })
        }

        const firstInvalidSection = getFirstInvalidCheckoutSection({
            contactIsValid,
            vehicleIsValid,
            disclaimerIsValid,
        })

        if (firstInvalidSection) {
            scrollToSection(firstInvalidSection)
            return false
        }

        const normalizedContactPayload = buildNormalizedContactPayload(currentValues)

        if (
            contactShouldEdit
            && shouldPauseForEmailDomainSuggestion(currentValues.email)
        ) {
            scrollToSection('contact')
            return false
        }

        const payload: UpdateReservationContactInfoMutationInput = {
            ...(contactShouldEdit ? normalizedContactPayload : {}),
            ...(vehicleShouldEdit ? buildVehiclePayload({
                values: currentValues,
                vehicleEntryMode: currentValues.vehicle_entry_mode,
                selectedSavedCar: getSelectedSavedCar({
                    currentUserCars,
                    selection: currentValues.vehicle_selection,
                }),
            }) : {}),
        }
        const beginPayment = () => onBeginPayment?.({
            name: currentValues.name,
            email: currentValues.email,
            phone_number: normalizedContactPayload.phone_number ?? '',
            phone_number_2: normalizedContactPayload.phone_number_2 ?? '',
        })

        if (Object.keys(payload).length === 0) {
            beginPayment()
            return true
        }

        try {
            const didPersist = await persistPayload({
                scope,
                payload,
            })

            if (!didPersist) {
                return false
            }

            syncSavedCheckoutSnapshots()
            beginPayment()
            return true
        } catch {
            return false
        }
    }

    const saveContactSection = async () => {
        const isValid = await validateContactSection()

        if (!isValid) {
            scrollToSection('contact')
            focusFirstContactError()
            return
        }

        const currentValues = form.getValues()
        const normalizedContactPayload = buildNormalizedContactPayload(currentValues)

        if (shouldPauseForEmailDomainSuggestion(currentValues.email)) {
            scrollToSection('contact')
            return
        }

        const nextSnapshot = buildSavedContactSnapshot(normalizedContactPayload)
        const didPersist = await persistPayload({
            scope: 'contact',
            payload: normalizedContactPayload,
        })

        if (!didPersist) {
            return
        }

        setContactSnapshot(nextSnapshot)
        setContactFormValues(form, nextSnapshot)
        clearEmailDomainSuggestion()
        setContactIsEditing(false)
    }

    const saveVehicleSection = async () => {
        const isValid = await validateVehicleSection()

        if (!isValid) {
            scrollToSection('vehicle')
            focusFirstVehicleError()
            return
        }

        const currentValues = form.getValues()
        const nextSnapshot = buildSavedVehicleSnapshot({
            values: currentValues,
            currentUserCars,
            shouldPrefillCheckoutInfo,
        })
        const didPersist = await persistPayload({
            scope: 'vehicle',
            payload: buildVehiclePayload({
                values: currentValues,
                vehicleEntryMode: currentValues.vehicle_entry_mode,
                selectedSavedCar: getSelectedSavedCar({
                    currentUserCars,
                    selection: currentValues.vehicle_selection,
                }),
            }),
        })

        if (!didPersist) {
            return
        }

        setVehicleSnapshot(nextSnapshot)
        setVehicleFormValues(form, nextSnapshot)
        setVehicleIsEditing(false)
    }

    const clearContactEdit = () => {
        setSectionFormValues(form, {
            ...normalizeSavedContactValues({}),
            requested_invoice: invoiceRequired,
            show_phone_number_2: false,
        }, CONTACT_SECTION_FIELD_NAMES)
        clearSectionErrors(form, CONTACT_SECTION_FIELD_NAMES)
        clearScopeError('contact')
        clearEmailDomainSuggestion()
    }

    const clearVehicleEdit = () => {
        setSectionFormValues(form, {
            number_plate: '',
            car_color: '',
            vehicle_entry_mode: VehicleEntryMode.now,
            vehicle_selection: 'custom',
        }, VEHICLE_SECTION_FIELD_NAMES)
        clearSectionErrors(form, VEHICLE_SECTION_FIELD_NAMES)
        clearScopeError('vehicle')
    }

    const cancelContactEdit = () => {
        setContactFormValues(form, contactSnapshot)
        clearSectionErrors(form, CONTACT_SECTION_FIELD_NAMES)
        clearScopeError('contact')
        clearEmailDomainSuggestion()
        setContactIsEditing(false)
    }

    const cancelVehicleEdit = () => {
        setVehicleFormValues(form, vehicleSnapshot)
        clearSectionErrors(form, VEHICLE_SECTION_FIELD_NAMES)
        clearScopeError('vehicle')
        setVehicleIsEditing(false)
    }

    const submitStripeCheckout = async () => {
        const isReadyForPayment = await prepareCheckoutForPayment('pay_stripe')

        if (!isReadyForPayment) {
            return
        }

        try {
            await continueToStripeCheckout({
                isPwa,
                onEmbeddedCheckout: onPay,
                createStripeCheckoutSession: async input => await runScopedAction({
                    scope: 'pay_stripe',
                    action: async () => await createStripeCheckoutSession.mutateAsync(input),
                }),
                onCheckoutError: () => {
                    setActionError({
                        scope: 'pay_stripe',
                        message: t('checkout.paymentResult.genericError'),
                    })
                },
                buildNativePaymentSheetInput: ({ paymentId, paymentIntentClientSecret }) => ({
                    paymentId,
                    paymentIntentClientSecret,
                    publishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
                    successUrl: getCheckoutSuccessPath({
                        isPwa: Boolean(isPwa),
                        locale: i18n.resolvedLanguage || i18n.language,
                        paymentId,
                        searchParams: location.search,
                    }),
                    cancelUrl: getCheckoutPath({
                        isPwa: Boolean(isPwa),
                        locale: i18n.resolvedLanguage || i18n.language,
                        searchParams: location.search,
                    }),
                }),
                onNativeCheckoutCompleted: paymentId => {
                    navigate(getCheckoutSuccessPath({
                        isPwa: Boolean(isPwa),
                        locale: i18n.resolvedLanguage || i18n.language,
                        paymentId,
                        searchParams: location.search,
                    }), { replace: true })
                },
            })
        } catch {
            return
        }
    }

    const submitBenefitCodeCheckout = async (benefitCode: string) => {
        const isReadyForPayment = await prepareCheckoutForPayment('pay_benefit_code')

        if (!isReadyForPayment) {
            return
        }

        try {
            const response = await runScopedAction({
                scope: 'pay_benefit_code',
                action: async () => await payByBenefitCode.mutateAsync(benefitCode),
            }) as BenefitCodePaymentPayload

            navigate(getCheckoutSuccessPath({
                isPwa: Boolean(isPwa),
                locale: i18n.resolvedLanguage || i18n.language,
                paymentId: response.payment_id,
                searchParams: location.search,
            }), { replace: true })
        } catch {
            return
        }
    }

    return {
        form,
        contact: {
            isEditing: contactIsEditing,
            shouldEdit: contactShouldEdit,
            summaryValues: contactSnapshot.summaryValues,
            defaultPhoneCountry,
            requestInProgress: requestInProgress && activeAction === 'contact',
            errorMessage: actionError.scope === 'contact' ? actionError.message : null,
            emailDomainSuggestion,
            sectionRef: contactSectionRef,
            openForEdit: () => {
                clearScopeError('contact')
                clearEmailDomainSuggestion()
                setContactIsEditing(true)
            },
            checkEmailDomainSuggestion,
            clearEmailDomainSuggestion,
            openForValidation: openContactForValidation,
            save: saveContactSection,
            selectPhoneCountry,
            clear: clearContactEdit,
            cancelEdit: cancelContactEdit,
        },
        vehicle: {
            isEditing: vehicleIsEditing,
            shouldEdit: vehicleShouldEdit,
            missingPlateDismissed: vehicleSnapshot.missingPlateDismissed,
            summaryValues: vehicleSnapshot.summaryValues,
            requestInProgress: requestInProgress && activeAction === 'vehicle',
            errorMessage: actionError.scope === 'vehicle' ? actionError.message : null,
            sectionRef: vehicleSectionRef,
            openForEdit: () => {
                clearScopeError('vehicle')
                setVehicleIsEditing(true)
            },
            openForValidation: openVehicleForValidation,
            save: saveVehicleSection,
            clear: clearVehicleEdit,
            cancelEdit: cancelVehicleEdit,
        },
        disclaimer: {
            sectionRef: disclaimerSectionRef,
        },
        pay: {
            stripe: {
                requestInProgress: requestInProgress && activeAction === 'pay_stripe',
                errorMessage: actionError.scope === 'pay_stripe' ? actionError.message : null,
                submit: submitStripeCheckout,
            },
            benefitCode: {
                requestInProgress: requestInProgress && activeAction === 'pay_benefit_code',
                errorMessage: actionError.scope === 'pay_benefit_code' ? actionError.message : null,
                submit: submitBenefitCodeCheckout,
            },
        },
        focusFirstInvalidField: (section: 'contact' | 'vehicle') => {
            if (section === 'contact') {
                focusFirstContactError()
                return
            }

            focusFirstVehicleError()
        },
    }
}

export function validateContactEmail(
    value: string,
    messages: CheckoutValidationMessages,
) {
    const trimmedValue = value.trim()

    if (trimmedValue === '') {
        return messages.requiredContact
    }

    return /\S+@\S+\.\S+/.test(trimmedValue)
        || messages.invalidContactEmail
}

export function validateContactRequiredField(
    value: string,
    messages: CheckoutValidationMessages,
) {
    return value.trim() !== '' || messages.requiredContact
}

export function validateContactPhone(
    value: string,
    defaultCountry: CountryCode,
    required: boolean,
    messages: CheckoutValidationMessages,
) {
    if (value.trim() === '') {
        return required ? messages.requiredContact : true
    }

    return isPossiblePhoneValue(value, defaultCountry)
        || messages.invalidContactPhone
}

export function validateVehicleNumberPlate(
    value: string,
    messages: CheckoutValidationMessages,
) {
    return value.trim() !== '' || messages.requiredVehicle
}

export function validateCheckoutDisclaimerAccepted(
    value: boolean,
    messages: CheckoutValidationMessages,
) {
    return value || messages.requiredDisclaimer
}

export function getContactValidationRule(
    fieldName: CheckoutContactInfoField,
    requestedInvoice: boolean,
    invoiceRequired: boolean,
    messages: CheckoutValidationMessages,
) {
    if (fieldName === 'email') {
        return (value: string) => validateContactEmail(value, messages)
    }

    if ((REQUIRED_CONTACT_FIELDS as ReadonlyArray<string>).includes(fieldName)) {
        return (value: string) => validateContactRequiredField(value, messages)
    }

    if (fieldName === 'invoice_country') {
        return (value: string) =>
            !requestedInvoice
            || isSupportedInvoiceCountryCode(value)
            || messages.requiredContact
    }

    if ((INVOICE_FIELDS as ReadonlyArray<string>).includes(fieldName)) {
        if (
            invoiceRequired
            && (OPTIONAL_INVOICE_FIELDS_WHEN_REQUIRED as ReadonlyArray<string>).includes(fieldName)
        ) {
            return undefined
        }

        return (value: string) => !requestedInvoice || validateContactRequiredField(value, messages)
    }

    return undefined
}

export function hasVehicleSummaryMissingNumberPlate({
    summaryValues,
    missingPlateDismissed,
}: {
    summaryValues: CheckoutVehicleInfoValues
    missingPlateDismissed: boolean
}) {
    return hasMissingVehicleNumberPlate(summaryValues) && !missingPlateDismissed
}
